import { useMutation } from 'react-query';
import React from 'react';
import { reportError } from '../../crash-reporter';
import { AssessmentTestResponse } from '../../api/teams';

const TIMEOUT = 1000 * 29; // 29 seconds because API Gateway timeout is 30 seconds
const POLL_INTERVAL = 1000 * 2; // 2 seconds

export function useLLMPlaygroundValidationPoller({
  getTestData,
  stepId,
  sectionId,
  onOutput,
}: {
  getTestData: () => Promise<AssessmentTestResponse>;
  stepId: string;
  sectionId: string;
  onOutput: (output: string) => void;
}) {
  const [isPolling, setIsPolling] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const pollMutation = useMutation({
    mutationFn: async () => {
      try {
        const newTestData = await getTestData();

        return newTestData;
      } catch (err) {
        setError(
          error
            ? 'Sorry, it seems something went wrong again. Our admin team has been notified. Feel free to move on.'
            : 'Something went wrong while validating your answer. Please try again.'
        );
        reportError(err as Error);
      }
    },
  });

  const pollTestData = React.useCallback(async () => {
    setIsPolling(true);
    setError(null);
    let hasUpdatedResult = false;
    let timePassed = 0;

    while (!hasUpdatedResult && timePassed < TIMEOUT) {
      const newestTestData = await pollMutation.mutateAsync();

      if (!newestTestData) {
        break;
      }
      const output =
        newestTestData.answers
          .find((answer) => answer.id === stepId)
          ?.sections.find(
            (
              section
            ): section is Extract<typeof section, { type: 'llm-playground' }> =>
              section.id === sectionId && section.type === 'llm-playground'
          )?.answer.output ?? '';

      if (output) {
        hasUpdatedResult = true;
        onOutput(output);
      }

      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
      timePassed += POLL_INTERVAL;

      if (timePassed >= TIMEOUT && !error) {
        setError(
          'Something went wrong while validating your answer. Please try again.'
        );
        reportError(new Error('Timeout while polling for validation'));
      }
    }

    setIsPolling(false);
  }, [
    pollMutation,
    setError,
    error,
    setIsPolling,
    sectionId,
    stepId,
    onOutput,
  ]);

  return {
    pollTestData,
    isPolling,
    error,
  };
}
